import { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from './components/shell/AppShell';
import { ColorProvider } from './contexts/ColorContext';
import { KeyboardShortcutsProvider } from './contexts/KeyboardShortcutsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToolStateProvider } from './contexts/ToolStateContext';
import { PaletteProvider } from './contexts/PaletteContext';
import { KeyboardShortcutsHelp } from './components/KeyboardShortcutsHelp';
import { FeedbackForm, FeedbackButton } from './components/FeedbackForm/FeedbackForm';
import type { UserFeedback } from './utils/userFeedback';
import { AlertProvider } from '@ascend/alert';

// Core tools (from design handoff)
import { FormatsTool } from './tools/FormatsTool';
import { NearestTool } from './tools/NearestTool';
import { EncyclopediaTool } from './tools/EncyclopediaTool';
import { PrintTool } from './tools/PrintTool';
import { SemanticTool } from './tools/SemanticTool';
import { CompareTool } from './tools/CompareTool';
import { HistoryTool } from './tools/HistoryTool';
import { FavoritesTool } from './tools/FavoritesTool';

// Additional features (from existing app)
import { PaletteTool } from './tools/PaletteTool';
import { AccessibilityTool } from './tools/AccessibilityTool';
import { TokensTool } from './tools/TokensTool';
import { EducationTool } from './tools/EducationTool';
import { SettingsTool } from './tools/SettingsTool';

// Promoted standalone tools
import { GradientTool } from './tools/GradientTool';
import { ExtractTool } from './tools/ExtractTool';
import { AuditTool } from './tools/AuditTool';
import { SubstrateTool } from './tools/SubstrateTool';
import { ScoreTool } from './tools/ScoreTool';
import { ExportTool } from './tools/ExportTool';

// Landing page
import { HomeTool } from './tools/HomeTool';

function App() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackConfig, setFeedbackConfig] = useState<{
    type?: UserFeedback['type'];
    category?: string;
    error?: string;
  }>({});

  const handleOpenFeedback = useCallback((
    type?: UserFeedback['type'],
    category?: string,
    error?: string,
  ) => {
    setFeedbackConfig({ type, category, error });
    setFeedbackOpen(true);
  }, []);

  const handleCloseFeedback = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackConfig({});
  }, []);

  return (
    <ThemeProvider>
      <AlertProvider>
      <KeyboardShortcutsProvider>
        <BrowserRouter>
          <ColorProvider>
            <ToolStateProvider>
            <PaletteProvider>
            <Routes>
              <Route element={<AppShell />}>
                <Route index element={<HomeTool />} />
                {/* Core tools */}
                <Route path="formats" element={<FormatsTool />} />
                <Route path="nearest" element={<NearestTool />} />
                <Route path="encyclopedia" element={<EncyclopediaTool />} />
                <Route path="print" element={<PrintTool />} />
                <Route path="semantic" element={<SemanticTool />} />
                <Route path="compare" element={<CompareTool />} />
                <Route path="history" element={<HistoryTool />} />
                <Route path="favorites" element={<FavoritesTool />} />
                {/* Additional features */}
                <Route path="palette" element={<PaletteTool />} />
                <Route path="accessibility" element={<AccessibilityTool />} />
                <Route path="education" element={<EducationTool />} />
                <Route path="settings" element={<SettingsTool />} />
                {/* Promoted standalone tools */}
                <Route path="gradient" element={<GradientTool />} />
                <Route path="extract" element={<ExtractTool />} />
                <Route path="audit" element={<AuditTool />} />
                <Route path="substrate" element={<SubstrateTool />} />
                <Route path="score" element={<ScoreTool />} />
                <Route path="export" element={<ExportTool />} />
                <Route path="tokens" element={<TokensTool />} />
                {/* Fallback */}
                <Route path="*" element={<Navigate to="/formats" replace />} />
              </Route>
            </Routes>
            </PaletteProvider>
            </ToolStateProvider>
            <KeyboardShortcutsHelp />
          </ColorProvider>

          {/* Feedback widget */}
          <FeedbackForm
            isOpen={feedbackOpen}
            onClose={handleCloseFeedback}
            initialType={feedbackConfig.type}
            initialCategory={feedbackConfig.category}
            initialError={feedbackConfig.error}
          />
          <FeedbackButton onOpen={handleOpenFeedback} />
        </BrowserRouter>
      </KeyboardShortcutsProvider>
      </AlertProvider>
    </ThemeProvider>
  );
}

export default App;
